import { useEffect, useState } from "react";

/* ------------------------------------------------------------------ *
 * A CARD TABLE'S TRANSIENT NOTICES — the «somebody just took something» moments a table announces
 * for a beat and then drops (بی‌دل's «… بی‌بی پیک را گرفت!» and its shot moon, 2026-08-29 →
 * 2026-10-08). They lived in `HeartsTable`; پاسور and شلم announce their own (a سور, a شلم call)
 * the same way, so the hook and the pill live here (session D, 2026-10-09).
 * ------------------------------------------------------------------ */

/** How long a transient notice stays up by default. Measured at ~2.4-2.5 s on the deployed bundle
 *  (بی‌دل's Q♠ corner notice); nudged up so it is comfortably readable without becoming a fixture —
 *  it fired every single hand. (Operator, 2026-08-29: «if you want to lengthen their duration a
 *  little, do that — but nothing else about them moves».) بی‌دل's two banners have since moved to
 *  their own, much longer hold (`MOON_BANNER_MS` in `HeartsTable`); this stays the default for
 *  whatever comes next. */
export const NOTICE_MS = 3600;

/**
 * THE ONE BANNER PILL — worn by BOTH of بی‌دل's banners (the shot moon and the Q♠), in the felt's
 * notices column.
 *
 * They are the two "somebody just took something" announcements and they are one design: same
 * shape, size, ink, animation and hold, differing only in their words.
 * ⚠️ THEY USED TO DIFFER IN COLOUR TOO — the Q♠ wore a translucent `danger-bluff` tint on the
 * argument that a penalty is not the rarest thing at the table. Operator's call, 2026-09-07: they
 * match. Keeping it as ONE constant is the point: two copies drifted apart once already, and a
 * test pins the two banners' classes equal.
 */
export const BANNER_PILL =
  "m-0 max-w-full truncate rounded-full border border-secondary/70 bg-gradient-to-br from-secondary to-gold-deep px-4 py-1.5 text-center font-label-md text-label-md text-ink-on-gold shadow-lg";

/**
 * A transient announcement («…بی‌بی پیک را گرفت!», the moon banner) — self-clears after `ms`;
 * keyed by a per-event signature so it fires exactly once per event (StrictMode-safe: the timer
 * re-arms on remount, the elapsed key sticks). The duration is a PARAMETER because the callers are
 * not all the same kind of moment — see بی‌دل's `MOON_BANNER_MS`. An empty signature is no event.
 */
export function useTransientNotice(sig: string, ms: number = NOTICE_MS): boolean {
  const [elapsedSig, setElapsedSig] = useState<string>("");
  useEffect(() => {
    if (!sig || elapsedSig === sig) return undefined;
    const timer = setTimeout(() => setElapsedSig(sig), ms);
    return () => clearTimeout(timer);
  }, [sig, elapsedSig, ms]);
  return sig !== "" && elapsedSig !== sig;
}
